/**
 * Tablet mode (PROD-1278 feedback, 2026-09-11): on a LANDSCAPE screen the serve
 * screen can trade the system keyboard for an on-screen keyboard in a left
 * column, so the rows stay in view while the worker (or the kid holding the
 * iPad) types. Portrait always keeps the system keyboard (raised by the
 * item-1 handoff bridge).
 *
 * Pure decisions, so the Today card tap and the serve page read ONE answer:
 *
 *   offered   = landscape, OR a touch tablet in portrait (>= 744px wide)   (the switch is on screen)
 *   defaultOn = coarse pointer AND (landscape AND >= 1024px, OR portrait AND >= 744px)
 *   enabled   = this device CLASS's remembered switch, else defaultOn
 *   active    = offered AND enabled
 *
 * The switch is offered on EVERY landscape screen, a desktop browser included
 * (Abner, 2026-09-11): anyone in landscape can turn the keyboard column on.
 * Since PROD-1428 round 5 a touch tablet in PORTRAIT is offered too (and on
 * by default): there the keyboard runs across the bottom of the screen,
 * letters left and the number pad right, instead of down a column. A phone
 * in portrait (every one is narrower than 744 CSS px, the iPad mini's
 * portrait width) is still the system keyboard.
 * The touch-tablet conditions only decide the DEFAULT: a landscape iPad opens
 * with it on, a computer opens with it off. The width guard in the default
 * keeps phones from opening two columns on their own: every iPad is at least
 * 1024 CSS px wide in landscape (the mini included), no phone is. Where
 * `matchMedia` is missing (SSR, the test DOM) the device is UNKNOWN: nothing
 * is offered and nothing is on, so no screen flashes the keyboard column
 * before the device is known.
 *
 * The remembered switch lives in localStorage under a key PER DEVICE CLASS
 * (touch vs pointer): a stand-mounted line iPad decides once, and a choice
 * made on a touch screen can never turn a computer on. That matters in one
 * browser too: DevTools emulating an iPad writes the touch key, the plain
 * window reads the pointer key, so a computer always opens off until someone
 * turns it on THERE. Reads and writes swallow storage errors (Safari private
 * mode, blocked site data) because a preference must never break the screen.
 */

export const TABLET_MODE_MIN_WIDTH_PX = 1024
/** The narrowest tablet in portrait (the iPad mini at 744 CSS px); anything narrower is a phone. */
export const TABLET_MODE_PORTRAIT_MIN_WIDTH_PX = 744
const TABLET_MODE_STORAGE_PREFIX = 'ordo.cafeteria.tabletMode'
/** The remembered switch of touch-screen devices (coarse primary pointer). */
export const TABLET_MODE_TOUCH_STORAGE_KEY = `${TABLET_MODE_STORAGE_PREFIX}.touch`
/** The remembered switch of mouse / trackpad devices (fine primary pointer). */
export const TABLET_MODE_POINTER_STORAGE_KEY = `${TABLET_MODE_STORAGE_PREFIX}.pointer`
/** The primary pointing device is a finger (an iPad, a touch kiosk). */
export const COARSE_POINTER_QUERY = '(pointer: coarse)'
export const LANDSCAPE_QUERY = '(orientation: landscape)'

export interface TabletModeEnvironment {
  /** `(pointer: coarse)`; null while unknown (no `matchMedia`). */
  coarsePointer: boolean | null
  /** `(orientation: landscape)`; null while unknown. */
  landscape: boolean | null
  /** `window.innerWidth` in CSS px; null while unknown. */
  viewportWidth: number | null
}

export const UNKNOWN_TABLET_MODE_ENVIRONMENT: TabletModeEnvironment = {
  coarsePointer: null,
  landscape: null,
  viewportWidth: null,
}

/** A touch screen in portrait at least a tablet wide: the bottom-keyboard layout's device. */
function portraitTouchTablet(env: TabletModeEnvironment): boolean {
  return (
    env.coarsePointer === true &&
    env.landscape === false &&
    env.viewportWidth !== null &&
    env.viewportWidth >= TABLET_MODE_PORTRAIT_MIN_WIDTH_PX
  )
}

/**
 * The "Tablet" switch is on screen: any landscape screen once the
 * orientation is known, or a touch tablet in portrait.
 */
export function tabletModeOffered(env: TabletModeEnvironment): boolean {
  return env.landscape === true || portraitTouchTablet(env)
}

/**
 * Where the switch starts ON for a device class that has not chosen: a
 * landscape touch tablet at least `TABLET_MODE_MIN_WIDTH_PX` wide, or a
 * portrait touch tablet. Unknown facts never qualify.
 */
export function tabletModeDefaultOn(env: TabletModeEnvironment): boolean {
  if (portraitTouchTablet(env)) {
    return true
  }
  return (
    env.coarsePointer === true &&
    env.landscape === true &&
    env.viewportWidth !== null &&
    env.viewportWidth >= TABLET_MODE_MIN_WIDTH_PX
  )
}

export function sameTabletModeEnvironment(a: TabletModeEnvironment, b: TabletModeEnvironment): boolean {
  return a.coarsePointer === b.coarsePointer && a.landscape === b.landscape && a.viewportWidth === b.viewportWidth
}

export type TabletModeWindow = Pick<Window, 'matchMedia' | 'innerWidth'>

function globalWindow(): TabletModeWindow | null {
  return typeof window === 'undefined' ? null : window
}

/**
 * The device facts, read synchronously. Callers that render from them must
 * call this in a state INITIALIZER, never only in an effect: an effect-first
 * read paints one frame of "unknown" first.
 */
export function readTabletModeEnvironment(win: TabletModeWindow | null = globalWindow()): TabletModeEnvironment {
  if (!win || typeof win.matchMedia !== 'function') {
    return UNKNOWN_TABLET_MODE_ENVIRONMENT
  }
  return {
    coarsePointer: win.matchMedia(COARSE_POINTER_QUERY).matches,
    landscape: win.matchMedia(LANDSCAPE_QUERY).matches,
    viewportWidth: typeof win.innerWidth === 'number' && win.innerWidth > 0 ? win.innerWidth : null,
  }
}

export type TabletModeStorage = Pick<Storage, 'getItem' | 'setItem'>

function globalStorage(): TabletModeStorage | null {
  // Safari with site data blocked throws on the ACCESS, not on the call.
  try {
    return typeof window === 'undefined' ? null : (window.localStorage ?? null)
  } catch {
    return null
  }
}

/**
 * Where this device class remembers its switch. Null while the pointer is
 * unknown: a choice cannot be filed under a class that is not known yet, so
 * nothing is read and nothing is written.
 */
export function tabletModeStorageKey(env: Pick<TabletModeEnvironment, 'coarsePointer'>): string | null {
  if (env.coarsePointer === null) {
    return null
  }
  return env.coarsePointer ? TABLET_MODE_TOUCH_STORAGE_KEY : TABLET_MODE_POINTER_STORAGE_KEY
}

/**
 * This device class's remembered "Tablet" switch: true or false once it has
 * chosen, null when it has not (or the class is unknown, or storage is
 * unavailable), so the default applies.
 */
export function readTabletModeChoice(
  env: Pick<TabletModeEnvironment, 'coarsePointer'>,
  storage: TabletModeStorage | null = globalStorage(),
): boolean | null {
  const key = tabletModeStorageKey(env)
  if (key === null) {
    return null
  }
  try {
    const value = storage?.getItem(key)
    return value === '1' ? true : value === '0' ? false : null
  } catch {
    return null
  }
}

export function writeTabletModeEnabled(
  env: Pick<TabletModeEnvironment, 'coarsePointer'>,
  enabled: boolean,
  storage: TabletModeStorage | null = globalStorage(),
): void {
  const key = tabletModeStorageKey(env)
  if (key === null) {
    return
  }
  try {
    storage?.setItem(key, enabled ? '1' : '0')
  } catch {
    // The switch still flips for this session; it just resets on reload.
  }
}

/** The switch as the page shows it: the remembered choice wins, else the device default. */
export function resolveTabletModeEnabled(choice: boolean | null, defaultOn: boolean): boolean {
  return choice ?? defaultOn
}
